import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  MenuCategory,
  MenuItem,
  CartItem,
  Order,
  DiningTable,
  QRTemplate,
  RestaurantProfile,
  CustomerProfile,
  OrderStatus,
  StaffRole,
  StaffUser,
  HeroSettings,
  HeroSlide,
  OfferBanner,
  LegalPagesSettings,
  Coupon,
  ThemeCustomization
} from '../types';
import { apiService } from '../services/apiService';
import {
  initialHeroSettings,
  initialHeroSlides,
  initialOfferBanners,
  initialLegalSettings,
  initialCoupons,
  initialThemeCustomization,
  initialStaffUsers
} from '../data/initialData';
import { playNewOrderChime, playChangeRequestAlert } from '../utils/audio';
import { calculateVatBreakdown } from '../utils/format';

interface AppContextType {
  // Navigation & View Mode
  currentView: string;
  setCurrentView: (view: any) => void;

  // Staff Roles & Auth
  currentStaffRole: StaffRole;
  activeStaffUser: StaffUser | null;
  staffUsers: StaffUser[];
  isStaffLoginOpen: boolean;
  setIsStaffLoginOpen: (open: boolean) => void;
  targetLoginRole: StaffRole;
  openStaffLogin: (role?: StaffRole) => void;
  loginStaff: (role: StaffRole, user?: StaffUser) => void;
  logoutStaff: () => void;
  addStaffUser: (user: Omit<StaffUser, 'id'>) => Promise<StaffUser>;
  updateStaffUser: (id: string, updates: Partial<StaffUser>) => Promise<void>;
  deleteStaffUser: (id: string) => Promise<void>;
  authenticateStaffUser: (credentials: {
    login_type?: 'pin' | 'password';
    pin_code?: string;
    identifier?: string;
    password?: string;
    role?: string;
  }) => Promise<{ success: boolean; user?: StaffUser; error?: string }>;

  // Theme & Live Brand Colors (Admin Configurable)
  theme: 'dark' | 'light';
  setTheme: (theme: 'dark' | 'light') => void;
  toggleTheme: () => void;
  themeCustomization: ThemeCustomization;
  saveThemeCustomization: (customization: ThemeCustomization) => Promise<void>;

  // Hero Carousel & Offer Banners (Customizable by Admin)
  heroSlides: HeroSlide[];
  saveHeroSlides: (slides: HeroSlide[]) => Promise<void>;
  addHeroSlide: (slide: Omit<HeroSlide, 'id'>) => Promise<void>;
  updateHeroSlide: (id: string, updates: Partial<HeroSlide>) => Promise<void>;
  deleteHeroSlide: (id: string) => Promise<void>;
  heroSettings: HeroSettings;
  saveHeroSettings: (settings: HeroSettings) => Promise<void>;
  offerBanners: OfferBanner[];
  saveOfferBanners: (banners: OfferBanner[]) => Promise<void>;

  // Smart Coupons Engine
  coupons: Coupon[];
  saveCoupons: (coupons: Coupon[]) => Promise<void>;
  appliedCoupon: Coupon | null;
  applyCoupon: (code: string) => { success: boolean; message: string };
  removeCoupon: () => void;
  couponDiscountAmount: number;

  // Legal Policies & Compliance (Admin Editable)
  legalSettings: LegalPagesSettings;
  saveLegalSettings: (settings: LegalPagesSettings) => Promise<void>;

  // Table & Customer Session
  currentTable: DiningTable | null;
  customerProfile: CustomerProfile | null;
  setCustomerProfile: (profile: CustomerProfile | null) => void;
  isCustomerWelcomeOpen: boolean;
  setIsCustomerWelcomeOpen: (open: boolean) => void;

  // Menu Data
  categories: MenuCategory[];
  refreshMenu: () => Promise<void>;
  toggleItemAvailability: (itemId: string, available: boolean) => Promise<void>;

  // Cart
  cart: CartItem[];
  addToCart: (item: MenuItem, quantity: number, variants: any[], instructions?: string) => void;
  removeFromCart: (cartItemId: string) => void;
  updateCartQuantity: (cartItemId: string, quantity: number) => void;
  clearCart: () => void;
  cartSubtotal: number;
  cartTotal: number;
  cartItemCount: number;
  isCartDrawerOpen: boolean;
  setIsCartDrawerOpen: (open: boolean) => void;

  // Active Customer Order Tracking & History
  activeCustomerOrder: Order | null;
  setActiveCustomerOrder: (order: Order | null) => void;
  isOrderHistoryOpen: boolean;
  setIsOrderHistoryOpen: (open: boolean) => void;
  openOrderHistory: () => void;
  submitCustomerOrder: (specialNotes?: string) => Promise<Order>;
  cancelCustomerOrder: (orderId: string) => Promise<void>;
  requestOrderChange: (orderId: string, notes: string) => Promise<void>;

  // Staff & Live Orders Management
  allOrders: Order[];
  refreshOrders: () => Promise<void>;
  updateOrderStatus: (orderId: string, status: OrderStatus) => Promise<void>;
  reviewOrderChange: (orderId: string, decision: 'approved' | 'rejected') => Promise<void>;
  updateOrder: (order: Order) => Promise<void>;

  // Tables & QR Studio
  diningTables: DiningTable[];
  qrTemplate: QRTemplate;
  saveQRTemplate: (template: QRTemplate) => Promise<void>;
  saveDiningTables: (tables: DiningTable[]) => Promise<void>;

  // Restaurant Profile & Footer (Admin Editable)
  restaurantProfile: RestaurantProfile;
  saveRestaurantProfile: (profile: RestaurantProfile) => Promise<void>;

  // Sound Customization
  soundEnabled: boolean;
  setSoundEnabled: (enabled: boolean) => void;
  orderSoundTheme: 'cash_register' | 'service_bell' | 'marimba_chord' | 'subtle_swoosh';
  saveOrderSoundTheme: (theme: 'cash_register' | 'service_bell' | 'marimba_chord' | 'subtle_swoosh') => void;
}

const AppContext = createContext<AppContextType | null>(null);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Active Staff User & Role Auth (Strictly gated by authentication)
  const [activeStaffUser, setActiveStaffUser] = useState<StaffUser | null>(() => {
    const saved = localStorage.getItem('ovenstory_active_staff_user');
    if (!saved) return null;
    try {
      return JSON.parse(saved);
    } catch {
      return null;
    }
  });

  const [currentStaffRole, setCurrentStaffRole] = useState<StaffRole>(() => {
    const savedUser = localStorage.getItem('ovenstory_active_staff_user');
    const savedRole = localStorage.getItem('ovenstory_staff_role');
    if (savedUser && (savedRole === 'kitchen' || savedRole === 'manager' || savedRole === 'admin')) {
      return savedRole;
    }
    return null;
  });

  const [currentView, setCurrentView] = useState<string>(() => {
    const pathname = window.location.pathname.toLowerCase();
    const params = new URLSearchParams(window.location.search);
    const viewParam = params.get('view');
    const roleParam = params.get('role');
    const tableParam = params.get('table') || params.get('t');
    const savedRole = localStorage.getItem('ovenstory_staff_role');
    const savedUser = localStorage.getItem('ovenstory_active_staff_user');

    if (pathname.includes('/kitchen') || roleParam === 'kitchen' ||
        pathname.includes('/manager') || roleParam === 'manager' ||
        pathname.includes('/admin') || roleParam === 'admin' ||
        pathname.includes('/staff')) {
      return 'staff-live';
    }
    if (viewParam && viewParam.startsWith('staff-')) return viewParam;

    if (savedRole && savedUser) {
      const savedView = localStorage.getItem('ovenstory_current_view');
      if (savedView && savedView.startsWith('staff-')) {
        return savedView;
      }
      return 'staff-live';
    }

    // When customer scans QR code on table, or specifies ?view=order, or visits /order
    if (tableParam || viewParam === 'order' || pathname.includes('/order')) {
      return 'customer';
    }

    // Default for everyone visiting the root site
    return 'landing';
  });

  const handleSetCurrentView = (view: string) => {
    setCurrentView(view);
    localStorage.setItem('ovenstory_current_view', view);
  };
  
  // Sound Theme Selection
  const [orderSoundTheme, setOrderSoundTheme] = useState<'cash_register' | 'service_bell' | 'marimba_chord' | 'subtle_swoosh'>(() => {
    const saved = localStorage.getItem('ovenstory_sound_theme_v1');
    return (saved as any) || 'cash_register';
  });
  const orderSoundThemeRef = React.useRef(orderSoundTheme);
  useEffect(() => {
    orderSoundThemeRef.current = orderSoundTheme;
  }, [orderSoundTheme]);

  const saveOrderSoundTheme = async (theme: 'cash_register' | 'service_bell' | 'marimba_chord' | 'subtle_swoosh') => {
    localStorage.setItem('ovenstory_sound_theme_v1', theme);
    orderSoundThemeRef.current = theme;
    setOrderSoundTheme(theme);
    try {
      await apiService.saveSettings({ order_sound_theme: theme });
    } catch (e) {
      console.warn('Could not save sound theme to backend:', e);
    }
  };

  const [staffUsers, setStaffUsers] = useState<StaffUser[]>(() => {
    const saved = localStorage.getItem('ovenstory_staff_users_v1');
    return saved ? JSON.parse(saved) : initialStaffUsers;
  });
  const [isStaffLoginOpen, setIsStaffLoginOpen] = useState(false);
  const [targetLoginRole, setTargetLoginRole] = useState<StaffRole>('kitchen');

  const addStaffUser = async (user: Omit<StaffUser, 'id'>) => {
    const created = await apiService.addStaffUser(user);
    setStaffUsers(prev => [...prev.filter(u => u.id !== created.id), created]);
    return created;
  };

  const updateStaffUser = async (id: string, updates: Partial<StaffUser>) => {
    await apiService.updateStaffUser(id, updates);
    setStaffUsers(prev => prev.map(u => (u.id === id ? { ...u, ...updates } : u)));
  };

  const deleteStaffUser = async (id: string) => {
    await apiService.deleteStaffUser(id);
    setStaffUsers(prev => prev.filter(u => u.id !== id));
  };

  const authenticateStaffUser = async (credentials: {
    login_type?: 'pin' | 'password';
    pin_code?: string;
    identifier?: string;
    password?: string;
    role?: string;
  }) => {
    return await apiService.authenticateStaff(credentials);
  };

  // Theme State (Dark / Light)
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    const saved = localStorage.getItem('ovenstory_theme');
    return (saved === 'light' || saved === 'dark') ? saved : 'dark';
  });

  // Dynamic Live Brand Color Customization (Admin Configurable)
  const [themeCustomization, setThemeCustomization] = useState<ThemeCustomization>(() => {
    const saved = localStorage.getItem('ovenstory_theme_customization_v1');
    return saved ? JSON.parse(saved) : initialThemeCustomization;
  });

  // Apply Live Brand Color directly to CSS root variables
  useEffect(() => {
    document.documentElement.style.setProperty('--accent-copper', themeCustomization.primary_color);
    document.documentElement.style.setProperty('--accent-copper-hover', themeCustomization.primary_color);
    localStorage.setItem('ovenstory_theme_customization_v1', JSON.stringify(themeCustomization));
  }, [themeCustomization]);

  const saveThemeCustomization = async (customization: ThemeCustomization) => {
    setThemeCustomization(customization);
    localStorage.setItem('ovenstory_theme_customization_v1', JSON.stringify(customization));
    await apiService.saveSettings({ theme_customization: customization });
  };

  useEffect(() => {
    localStorage.setItem('ovenstory_theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));
  };

  // Hero Slides (Multi-Slide Carousel)
  const [heroSlides, setHeroSlides] = useState<HeroSlide[]>(() => {
    const saved = localStorage.getItem('ovenstory_hero_slides_v1');
    return saved ? JSON.parse(saved) : initialHeroSlides;
  });

  const saveHeroSlides = async (slides: HeroSlide[]) => {
    localStorage.setItem('ovenstory_hero_slides_v1', JSON.stringify(slides));
    setHeroSlides(slides);
    await apiService.saveSettings({ hero_slides: slides });
  };

  const addHeroSlide = async (slideData: Omit<HeroSlide, 'id'>) => {
    const newSlide: HeroSlide = {
      ...slideData,
      id: `slide_${Date.now()}`
    };
    const updated = [...heroSlides, newSlide];
    await saveHeroSlides(updated);
  };

  const updateHeroSlide = async (id: string, updates: Partial<HeroSlide>) => {
    const updated = heroSlides.map(s => (s.id === id ? { ...s, ...updates } : s));
    await saveHeroSlides(updated);
  };

  const deleteHeroSlide = async (id: string) => {
    const updated = heroSlides.filter(s => s.id !== id);
    await saveHeroSlides(updated);
  };

  // Hero & Offer Banners
  const [heroSettings, setHeroSettings] = useState<HeroSettings>(() => {
    const saved = localStorage.getItem('ovenstory_hero_settings_v1');
    return saved ? JSON.parse(saved) : initialHeroSettings;
  });

  const [offerBanners, setOfferBanners] = useState<OfferBanner[]>(() => {
    const saved = localStorage.getItem('ovenstory_offer_banners_v1');
    return saved ? JSON.parse(saved) : initialOfferBanners;
  });

  // Coupons
  const [coupons, setCoupons] = useState<Coupon[]>(() => {
    const saved = localStorage.getItem('ovenstory_coupons_v1');
    return saved ? JSON.parse(saved) : initialCoupons;
  });
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(() => {
    const saved = localStorage.getItem('ovenstory_applied_coupon');
    return saved ? JSON.parse(saved) : null;
  });

  const [legalSettings, setLegalSettings] = useState<LegalPagesSettings>(() => {
    const saved = localStorage.getItem('ovenstory_legal_settings_v1');
    return saved ? JSON.parse(saved) : initialLegalSettings;
  });

  const saveHeroSettings = async (settings: HeroSettings) => {
    localStorage.setItem('ovenstory_hero_settings_v1', JSON.stringify(settings));
    setHeroSettings(settings);
    await apiService.saveSettings({ hero_settings: settings });
  };

  const saveOfferBanners = async (banners: OfferBanner[]) => {
    localStorage.setItem('ovenstory_offer_banners_v1', JSON.stringify(banners));
    setOfferBanners(banners);
    await apiService.saveSettings({ offer_banners: banners });
  };

  const saveCoupons = async (c: Coupon[]) => {
    localStorage.setItem('ovenstory_coupons_v1', JSON.stringify(c));
    setCoupons(c);
    await apiService.saveSettings({ coupons: c });
  };

  const saveLegalSettings = async (settings: LegalPagesSettings) => {
    localStorage.setItem('ovenstory_legal_settings_v1', JSON.stringify(settings));
    setLegalSettings(settings);
    await apiService.saveSettings({ legal_settings: settings });
  };

  // Locked Table from QR / URL
  const [currentTable, setCurrentTable] = useState<DiningTable | null>(null);

  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(() => {
    const saved = localStorage.getItem('ovenstory_customer');
    return saved ? JSON.parse(saved) : null;
  });
  const [isCustomerWelcomeOpen, setIsCustomerWelcomeOpen] = useState(false);

  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [cart, setCart] = useState<CartItem[]>(() => {
    const saved = localStorage.getItem('ovenstory_cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);

  const [activeCustomerOrder, setActiveCustomerOrder] = useState<Order | null>(() => {
    const saved = localStorage.getItem('ovenstory_active_order');
    return saved ? JSON.parse(saved) : null;
  });
  const [isOrderHistoryOpen, setIsOrderHistoryOpen] = useState(false);
  const openOrderHistory = () => {
    setIsOrderHistoryOpen(false);
    setCurrentView('order-history');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [allOrders, setAllOrders] = useState<Order[]>([]);
  const [diningTables, setDiningTables] = useState<DiningTable[]>([]);
  const [qrTemplate, setQrTemplate] = useState<QRTemplate>(apiService.getRestaurantProfile as any);
  const [restaurantProfile, setRestaurantProfile] = useState<RestaurantProfile>(apiService.getRestaurantProfile());
  const [soundEnabled, setSoundEnabled] = useState(true);

  // 1. Initial Load & Role / Table URL Detection
  useEffect(() => {
    const loadInitial = async () => {
      const menuData = await apiService.getMenu();
      setCategories(menuData);

      const tableData = await apiService.getTables();
      setDiningTables(tableData.tables);
      setQrTemplate(tableData.template);

      const ordersData = await apiService.getOrders();
      setAllOrders(ordersData);

      const staffData = await apiService.getStaffUsers();
      if (Array.isArray(staffData) && staffData.length > 0) {
        setStaffUsers(staffData);
      }

      const settingsData = await apiService.getSettings();
      if (settingsData) {
        if (settingsData.order_sound_theme) {
          setOrderSoundTheme(settingsData.order_sound_theme);
          orderSoundThemeRef.current = settingsData.order_sound_theme;
          localStorage.setItem('ovenstory_sound_theme_v1', settingsData.order_sound_theme);
        }
        if (settingsData.hero_slides !== undefined && Array.isArray(settingsData.hero_slides)) {
          setHeroSlides(settingsData.hero_slides);
          localStorage.setItem('ovenstory_hero_slides_v1', JSON.stringify(settingsData.hero_slides));
        }
        if (settingsData.offer_banners !== undefined && Array.isArray(settingsData.offer_banners)) {
          setOfferBanners(settingsData.offer_banners);
          localStorage.setItem('ovenstory_offer_banners_v1', JSON.stringify(settingsData.offer_banners));
        }
        if (settingsData.hero_settings) {
          setHeroSettings(settingsData.hero_settings);
          localStorage.setItem('ovenstory_hero_settings_v1', JSON.stringify(settingsData.hero_settings));
        }
        if (settingsData.coupons !== undefined && Array.isArray(settingsData.coupons)) {
          setCoupons(settingsData.coupons);
          localStorage.setItem('ovenstory_coupons_v1', JSON.stringify(settingsData.coupons));
        }
        if (settingsData.theme_customization) {
          setThemeCustomization(settingsData.theme_customization);
          localStorage.setItem('ovenstory_theme_customization_v1', JSON.stringify(settingsData.theme_customization));
        }
        if (settingsData.legal_settings) {
          setLegalSettings(settingsData.legal_settings);
          localStorage.setItem('ovenstory_legal_settings_v1', JSON.stringify(settingsData.legal_settings));
        }
      }

      // Check URL parameters for Table e.g. ?table=3 or ?t=3
      const params = new URLSearchParams(window.location.search);
      const tableParam = params.get('table') || params.get('t');
      if (tableParam) {
        const num = parseInt(tableParam, 10);
        const match = tableData.tables.find(t => t.table_number === num);
        if (match) {
          setCurrentTable(match);
        } else {
          setCurrentTable({
            id: `tbl_${num}`,
            table_number: num,
            label: `Pöytä ${num}`,
            capacity: 4,
            zone: 'Dining Area',
            is_active: true
          });
        }
      } else {
        setCurrentTable(tableData.tables[0] || {
          id: 'tbl_1',
          table_number: 1,
          label: 'Pöytä 1',
          capacity: 4,
          zone: 'Main Dining',
          is_active: true
        });
      }

      // Check URL for role entry points - require authentication if not logged in
      const roleParam = params.get('role');
      const pathname = window.location.pathname.toLowerCase();

      let targetRole: StaffRole | null = null;
      if (roleParam === 'kitchen' || pathname.includes('/kitchen')) targetRole = 'kitchen';
      else if (roleParam === 'manager' || pathname.includes('/manager')) targetRole = 'manager';
      else if (roleParam === 'admin' || pathname.includes('/admin')) targetRole = 'admin';
      else if (pathname.includes('/staff')) targetRole = 'kitchen';

      const savedUser = localStorage.getItem('ovenstory_active_staff_user');
      if (targetRole) {
        setTargetLoginRole(targetRole);
        if (!savedUser) {
          setCurrentStaffRole(null);
          setActiveStaffUser(null);
          setIsStaffLoginOpen(true);
        } else {
          try {
            const parsedUser: StaffUser = JSON.parse(savedUser);
            if (parsedUser && parsedUser.role) {
              setCurrentStaffRole(parsedUser.role as StaffRole);
            }
          } catch {
            setCurrentStaffRole(null);
            setActiveStaffUser(null);
            setIsStaffLoginOpen(true);
          }
        }
      }
    };
    loadInitial();
  }, []);

  const openStaffLogin = (role: StaffRole = 'kitchen') => {
    setTargetLoginRole(role);
    setIsStaffLoginOpen(true);
  };

  const loginStaff = (role: StaffRole, user?: StaffUser) => {
    setCurrentStaffRole(role);
    localStorage.setItem('ovenstory_staff_role', role || '');
    if (user) {
      setActiveStaffUser(user);
      localStorage.setItem('ovenstory_active_staff_user', JSON.stringify(user));
    }
    setCurrentView('staff-live');
    localStorage.setItem('ovenstory_current_view', 'staff-live');
    if (window.location.pathname !== `/${role}`) {
      window.history.replaceState(null, '', `/${role}`);
    }
  };

  const logoutStaff = () => {
    setCurrentStaffRole(null);
    setActiveStaffUser(null);
    localStorage.removeItem('ovenstory_staff_role');
    localStorage.removeItem('ovenstory_active_staff_user');
    localStorage.removeItem('ovenstory_current_view');
    setCurrentView('customer');
    if (window.location.pathname !== '/') {
      window.history.replaceState(null, '', '/');
    }
  };

  const refreshOrders = async () => {
    try {
      const orders = await apiService.getOrders();
      setAllOrders(orders);
    } catch (e) {
      console.error('Error refreshing orders:', e);
    }
  };

  const refreshMenu = async () => {
    try {
      const menu = await apiService.getMenu();
      setCategories(menu);
    } catch (e) {
      console.error('Error refreshing menu:', e);
    }
  };

  const activeCustomerOrderRef = React.useRef<Order | null>(activeCustomerOrder);
  useEffect(() => {
    activeCustomerOrderRef.current = activeCustomerOrder;
  }, [activeCustomerOrder]);

  // Real-Time Live Order Polling (Every 2 seconds for instant Kitchen & Customer synchronization)
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const latestOrders = await apiService.getOrders();
        
        // 1. Update Staff Board & Play Chimes
        setAllOrders(prev => {
          const prevSubmittedIds = new Set(prev.filter(o => o.status === 'submitted').map(o => o.id));
          const newSubmitted = latestOrders.filter(o => o.status === 'submitted' && !prevSubmittedIds.has(o.id));
          
          if (newSubmitted.length > 0 && currentStaffRole && soundEnabled) {
            playNewOrderChime(orderSoundThemeRef.current);
          }
          return latestOrders;
        });

        // 2. Update Customer Live Tracker Screen Instantly
        const currentActive = activeCustomerOrderRef.current;
        if (currentActive && currentActive.id) {
          const matching = latestOrders.find(o => o.id === currentActive.id);
          if (matching) {
            if (
              matching.status !== currentActive.status ||
              matching.change_status !== currentActive.change_status
            ) {
              setActiveCustomerOrder(matching);
              localStorage.setItem('ovenstory_active_order', JSON.stringify(matching));
            }
          }
        }
      } catch (e) {
        // silent fail on poll
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [currentStaffRole, soundEnabled, orderSoundTheme]);

  // Persist Cart
  useEffect(() => {
    localStorage.setItem('ovenstory_cart', JSON.stringify(cart));
  }, [cart]);

  // Persist Applied Coupon
  useEffect(() => {
    if (appliedCoupon) {
      localStorage.setItem('ovenstory_applied_coupon', JSON.stringify(appliedCoupon));
    } else {
      localStorage.removeItem('ovenstory_applied_coupon');
    }
  }, [appliedCoupon]);

  // Persist Active Order
  useEffect(() => {
    if (activeCustomerOrder) {
      localStorage.setItem('ovenstory_active_order', JSON.stringify(activeCustomerOrder));
    } else {
      localStorage.removeItem('ovenstory_active_order');
    }
  }, [activeCustomerOrder]);

  const addToCart = (item: MenuItem, quantity: number, variants: any[], instructions?: string) => {
    const variantExtra = variants.reduce((sum, v) => sum + (v.price || 0), 0);
    const unitPrice = item.price + variantExtra;
    const cartItemId = `${item.id}_${Date.now()}_${Math.random()}`;

    const newCartItem: CartItem = {
      cart_item_id: cartItemId,
      menu_item: item,
      quantity,
      selected_variants: variants,
      special_instructions: instructions,
      unit_price: unitPrice,
      total_price: unitPrice * quantity
    };

    setCart(prev => [...prev, newCartItem]);
  };

  const removeFromCart = (cartItemId: string) => {
    setCart(prev => prev.filter(item => item.cart_item_id !== cartItemId));
  };

  const updateCartQuantity = (cartItemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(cartItemId);
      return;
    }
    setCart(prev => prev.map(item => {
      if (item.cart_item_id === cartItemId) {
        return {
          ...item,
          quantity,
          total_price: item.unit_price * quantity
        };
      }
      return item;
    }));
  };

  const clearCart = () => {
    setCart([]);
    setAppliedCoupon(null);
  };

  const cartSubtotal = cart.reduce((sum, item) => sum + item.total_price, 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Calculate Coupon Discount Amount with Conditions Check
  const calculateCouponDiscount = (): number => {
    if (!appliedCoupon) return 0;
    if (cartSubtotal < appliedCoupon.min_order_amount) return 0;

    let discount = 0;
    if (appliedCoupon.discount_type === 'percentage') {
      discount = (cartSubtotal * appliedCoupon.discount_value) / 100;
      if (appliedCoupon.max_discount_amount && discount > appliedCoupon.max_discount_amount) {
        discount = appliedCoupon.max_discount_amount;
      }
    } else {
      discount = appliedCoupon.discount_value;
    }

    return Math.min(discount, cartSubtotal);
  };

  const couponDiscountAmount = calculateCouponDiscount();
  const cartTotal = Math.max(0, cartSubtotal - couponDiscountAmount);

  // Apply Coupon with Conditions Matcher (Day, Time, Min Order, Active state)
  const applyCoupon = (code: string): { success: boolean; message: string } => {
    const trimmed = code.trim().toUpperCase();
    const found = coupons.find(c => c.code.toUpperCase() === trimmed && c.is_active);

    if (!found) {
      return { success: false, message: 'Virheellinen tai vanhentunut kuponkikoodi / Invalid coupon code' };
    }

    if (cartSubtotal < found.min_order_amount) {
      return {
        success: false,
        message: `Minimitilausmäärä tälle kupongille on ${found.min_order_amount.toFixed(2)} € (Ostoskorisi: ${cartSubtotal.toFixed(2)} €)`
      };
    }

    // Day of week check
    const currentDay = new Date().getDay();
    if (found.valid_days && !found.valid_days.includes(currentDay)) {
      return {
        success: false,
        message: 'Tämä kuponki ei ole voimassa tänään / Not valid today'
      };
    }

    // Time of day check
    if (found.valid_start_time && found.valid_end_time) {
      const now = new Date();
      const currentHoursMins = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      if (currentHoursMins < found.valid_start_time || currentHoursMins > found.valid_end_time) {
        return {
          success: false,
          message: `Kuponki voimassa vain klo ${found.valid_start_time}–${found.valid_end_time} / Valid only between ${found.valid_start_time}–${found.valid_end_time}`
        };
      }
    }

    setAppliedCoupon(found);
    return {
      success: true,
      message: `Kuponki "${found.code}" aktivoitu! Alennus: -${found.discount_type === 'percentage' ? `${found.discount_value}%` : `${found.discount_value.toFixed(2)} €`}`
    };
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
  };

  const submitCustomerOrder = async (specialNotes?: string): Promise<Order> => {
    if (!currentTable) throw new Error('Pöytää ei ole valittu');
    const customerName = customerProfile?.name || 'Asiakas';
    const customerPhone = customerProfile?.phone || '+358401234567';

    const vatItems = cart.map(c => ({
      price: c.unit_price,
      quantity: c.quantity,
      vat_rate: c.menu_item.vat_rate
    }));
    const vatCalc = calculateVatBreakdown(vatItems);

    const orderNumber = `#${Math.floor(1000 + Math.random() * 9000)}`;
    const orderId = `ord_${Date.now()}`;

    const newOrder: Order = {
      id: orderId,
      order_number: orderNumber,
      table_id: currentTable.id,
      table_number: currentTable.table_number,
      customer_name: customerName,
      customer_phone: customerPhone,
      status: 'submitted',
      total_amount: cartTotal,
      discount_amount: couponDiscountAmount > 0 ? couponDiscountAmount : undefined,
      coupon_code: appliedCoupon ? appliedCoupon.code : undefined,
      vat_food_amount: vatCalc.foodVat,
      vat_beverage_amount: vatCalc.beverageVat,
      special_notes: specialNotes,
      guest_count: customerProfile?.guest_count || 2,
      created_at: new Date().toISOString(),
      items: cart.map(c => ({
        id: `oi_${Date.now()}_${Math.random()}`,
        order_id: orderId,
        item_id: c.menu_item.id,
        name: c.menu_item.name_fi,
        price: c.unit_price,
        quantity: c.quantity,
        selected_variants: c.selected_variants.map(v => ({
          group_title: v.group_title,
          option_name: v.option_name,
          price: v.price
        })),
        special_instructions: c.special_instructions,
        vat_rate: c.menu_item.vat_rate
      }))
    };

    const saved = await apiService.createOrder(newOrder);
    setActiveCustomerOrder(saved);
    clearCart();
    setIsCartDrawerOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (soundEnabled) {
      playNewOrderChime(orderSoundThemeRef.current);
    }

    await refreshOrders();
    return saved;
  };

  const cancelCustomerOrder = async (orderId: string) => {
    await apiService.cancelOrder(orderId);
    setActiveCustomerOrder(null);
    await refreshOrders();
  };

  const requestOrderChange = async (orderId: string, notes: string) => {
    await apiService.requestOrderChange(orderId, notes);
    if (activeCustomerOrder && activeCustomerOrder.id === orderId) {
      setActiveCustomerOrder({
        ...activeCustomerOrder,
        change_status: 'requested',
        change_request_notes: notes
      });
    }
    await refreshOrders();
  };

  const updateOrderStatus = async (orderId: string, status: OrderStatus) => {
    await apiService.updateOrderStatus(orderId, status);
    await refreshOrders();
  };

  const reviewOrderChange = async (orderId: string, decision: 'approved' | 'rejected') => {
    await apiService.reviewOrderChange(orderId, decision);
    await refreshOrders();
  };

  const updateOrder = async (order: Order) => {
    await apiService.updateOrder(order);
    if (activeCustomerOrder && activeCustomerOrder.id === order.id) {
      setActiveCustomerOrder(order);
    }
    await refreshOrders();
  };

  const toggleItemAvailability = async (itemId: string, available: boolean) => {
    await apiService.toggleItemAvailability(itemId, available);
    await refreshMenu();
  };

  const saveQRTemplate = async (template: QRTemplate) => {
    await apiService.saveQRTemplate(template);
    setQrTemplate(template);
  };

  const saveDiningTables = async (tables: DiningTable[]) => {
    await apiService.saveTables(tables);
    setDiningTables(tables);
  };

  const saveRestaurantProfile = async (profile: RestaurantProfile) => {
    apiService.saveRestaurantProfile(profile);
    setRestaurantProfile(profile);
  };

  return (
    <AppContext.Provider
      value={{
        currentView,
        setCurrentView: handleSetCurrentView,
        currentStaffRole,
        activeStaffUser,
        staffUsers,
        isStaffLoginOpen,
        setIsStaffLoginOpen,
        targetLoginRole,
        openStaffLogin,
        loginStaff,
        logoutStaff,
        addStaffUser,
        updateStaffUser,
        deleteStaffUser,
        authenticateStaffUser,
        theme,
        setTheme,
        toggleTheme,
        themeCustomization,
        saveThemeCustomization,
        heroSlides,
        saveHeroSlides,
        addHeroSlide,
        updateHeroSlide,
        deleteHeroSlide,
        heroSettings,
        saveHeroSettings,
        offerBanners,
        saveOfferBanners,
        coupons,
        saveCoupons,
        appliedCoupon,
        applyCoupon,
        removeCoupon,
        couponDiscountAmount,
        legalSettings,
        saveLegalSettings,
        currentTable,
        customerProfile,
        setCustomerProfile,
        isCustomerWelcomeOpen,
        setIsCustomerWelcomeOpen,
        categories,
        refreshMenu,
        toggleItemAvailability,
        cart,
        addToCart,
        removeFromCart,
        updateCartQuantity,
        clearCart,
        cartSubtotal,
        cartTotal,
        cartItemCount,
        isCartDrawerOpen,
        setIsCartDrawerOpen,
        activeCustomerOrder,
        setActiveCustomerOrder,
        isOrderHistoryOpen,
        setIsOrderHistoryOpen,
        openOrderHistory,
        submitCustomerOrder,
        cancelCustomerOrder,
        requestOrderChange,
        allOrders,
        refreshOrders,
        updateOrderStatus,
        reviewOrderChange,
        updateOrder,
        diningTables,
        qrTemplate,
        saveQRTemplate,
        saveDiningTables,
        restaurantProfile,
        saveRestaurantProfile,
        soundEnabled,
        setSoundEnabled,
        orderSoundTheme,
        saveOrderSoundTheme
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within an AppProvider');
  return context;
};
